import { useMemo, useState } from "react";

/**
 * @type Predicated is a function that takes in a filter item and a current filter state and returns a boolean
 * that is true if the item passes the filter check and false if it does not 
 *
 * ex We have tags [brass, strings] we want to filter by [strings] predicate should return true
 *
 */
export type Predicate<Item, State> = (item: Item, state: State) => boolean;

/**
 * Hook for filtering through a list of items
 *
 * @returns Array of items that pass filter check - memoizes inputs
 *
 * @param items 
 * This is the list of items you want to filter through - ex list of marketplace items
 *
 * @param state 
 * This is the current state of the filter - ex const FilterState = { wantTags: string[], search: string };
 *
 * @param predicates 
 * This is an array of funtions that your items must all pass to be inlcuded in the resultant list of items that pass the filter checks
 * See documentation on the [Predicate] type for more information
 */
export function useFilteredList<Item, State>(
  items: Item[],
  state: State,
  predicates: Array<Predicate<Item, State>>,
) {
  return useMemo(
    () => items.filter((it) => predicates.every((p) => p(it, state))),
    [items, state, predicates],
  );
}

/**
 * Hook for setting filter state 
 *
 * @param initial 
 * The initial filter state as defined by the user ex - 
 * const FilterState = {
 *  categories: string[];
 *  developers: string[];
 *  q: string;
 * }
 *
 * @returns 
 * state - current filter state
 * set - function that returns a setState function for setting the filterState
 *    ex - set('q') [returns (setState<string>)]
 * setState - function that can override the internal state provided by [initial]
 *
 */
export function useFilterState<State extends object>(initial: State) {
  const [state, setState] = useState<State>(initial);

  function set<K extends keyof State>(key: K) {
    return (value: State[K]) =>
      setState((prev) => ({ ...prev, [key]: value }));
  }

  return { state, set, setState };
}

// some helper functions for commons filter states
export const norm = (s?: string) => (s ?? "").toLowerCase().trim();
export const contains = (word?: string, searchStr?: string) =>
  norm(word).includes(norm(searchStr));

/**
* Helper function that returns a predicated for filtering by something such as tags
*
* This one returns true if all active tags match if 'all' is true else returns true if atleast one matches
*
* @param getValues 
* A function that, given the item, returns that values we want to search through in an array
*
* @param getActive
* A function that, given the current filter state, returns the values we currently have as active filters
*
* @param all 
* A boolean value that marks whether we want to have all active state match the values or just one 
*
* @param normalize 
* A function to normalize the search filter state items and active items for comparison - optional
*
* ex collection has tags [brass, string] filter will match on the following states WITH all enabled:
* [brass]
* [string]
* [brass,string]  
*
* ex collection has tags [brass, string] filter will match on the following states WITHOUT all enabled:
* [brass, percussion] <- note that percussion isnt included in the tags but still matches because at least one of these (brass) matched the tags
* [string]
* [brass,string]
*/
export function multiSelect<Item, V>(
  getValues: (item: Item) => readonly V[] | V[] | undefined,
  getActive: (state: any) => readonly V[] | V[] | undefined,
  all: boolean,
  normalize?: (v: V) => string
): Predicate<Item, any> {
  return (item, state) => {
    const active = getActive(state) ?? [];
    if (active.length === 0) return true;

    const values = getValues(item) ?? [];
    if (normalize) {
      const set = new Set(values.map(normalize));
      return all ? active.every((v) => set.has(normalize(v))) : active.some((v) => set.has(normalize(v)));
    }
    const set = new Set(values as V[]);
    return all ? active.every((v) => set.has(v)) : active.some((v) => set.has(v));
  };
}

/** 
 * Helper function that returns a predicate for filtering text
 *
 * @param getSearch 
 * A function that, given the current filterstate, returns the search term out of the state
 *
 * @param fields 
 * An array of fields to search through
 *
 * this provides a way to search text through multiple fields
* ex current search string is "test pr" and we passed two items to the array:
* [
*   (item) => item.title
*   (item) => item.tags.join(" ")
* ]
* this will match the item.title if it is something like "Test product"
* or will match the tags if the tags have "test product" contained
*/
export function textSearch<Item, State>(
  getSearch: (state: State) => string, // we just need to let the predicate know how to get the current search string out of the provided state
  fields: Array<(item: Item) => string | undefined>, // array of fields to search through
): Predicate<Item, State> {
  return (item, state) => {
    const q = norm(getSearch(state));
    if (!q) return true;
    return fields.some((get) => contains(get(item), q));
  };
}
